import React from "react";

import { css, cx } from "@emotion/css";
import Typography from "@mui/material/Typography";
import { useRouter } from "@tanstack/react-router";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type BasePageHeaderProps = {
  title?: string;
  content?: React.ReactNode;
  back?: boolean;
  stickyHeader?: boolean;
};

export type PageHeaderProps = AtomProps<
  BasePageHeaderProps,
  StyleClasses<"root" | "stickyHeader" | "back" | "content">
>;

export const PageHeader: React.FC<PageHeaderProps> = ({
  id,
  title,
  content,
  back,
  stickyHeader = false,
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);
  const router = useRouter();

  return (
    <>
      {title && <title>{`Stēto - ${title}`}</title>}
      <div
        id={id}
        className={cx(
          {
            [styles.stickyHeader]: stickyHeader,
          },
          styles.root,
          className,
          classes?.root,
          (styles.stickyHeader && classes?.stickyHeader) || undefined,
        )}
        {...props}
      >
        {back ? (
          <Icons.arrowLeft className={cx(styles.goBack, classes?.back)} onClick={() => router.history.back()} />
        ) : null}
        {title && <Typography variant="h4">{title}</Typography>}
        {content && <div className={cx(styles.content, classes?.content)}>{content}</div>}
      </div>
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: `2vh 2vw ${theme.spacing(12)} 2vw`,
  }),
  stickyHeader: css({
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: theme.palette.background.default,
  }),
  goBack: css({
    fill: theme.palette.grey[900],
    width: `calc(${theme.spacing(8)} * 2 + ${FontSizes.h4})`,
    height: `calc(${theme.spacing(4)} * 2 + ${FontSizes.h4})`,
    padding: theme.spacing(2, 4),
    marginRight: theme.spacing(8),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(4),
    },
  }),
  content: css({
    display: "flex",
    flexDirection: "row",
    flex: "1 1",
    justifyContent: "flex-end",
  }),
});
